import React, { useCallback, useContext, useMemo, useState } from 'react'
import { withRouter } from 'react-router-dom'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import DataTable from '../../../ui/DataTable/DataTable'
import Spinner from '../../../ui/Spinner/Spinner'
import Button from '../../../ui/Button/Button'
import { formatDateAsDayMontYearHHMM } from '../../../utils/date/dateFormat'
import resolveCampaignLinkResponsibleLabel from '../../../api/marketing/resolveCampaignLinkResponsibleLabel'
import ShortLinkCell from './ShortLinkCell'
import LinkContextCell from './LinkContextCell'
import ActiveStatusBadge from '../../../ui/ActiveStatusBadge/ActiveStatusBadge'
import LinksRowActions from './LinksRowActions'
import DeleteCampaignLinkPopUp from './DeleteCampaignLinkPopUp'
import { AuthContext } from '../../../App'
import { canEditCampaignLink } from '../../../config/accessControl'
import './LinksTable.sass'

const CHANNEL_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  telegram: 'Telegram',
  google: 'Google',
  email: 'Email',
  partner: 'Partner',
  other: 'Other',
}

const FORMAT_LABELS = {
  profile: 'Profile',
  story: 'Story',
  post: 'Post',
  reel: 'Reel',
  ad: 'Ad',
  banner_ad: 'Banner Ad',
  email: 'Email',
  partner_post: 'Partner Post',
  other: 'Other',
}

const DIRECTION_LABELS = {
  home: 'Головна',
  pdp: 'Товар / PDP',
  category: 'Категорія',
  brand: 'Бренд',
  outlet: 'Outlet',
  promo: 'Промо / Акція',
  landing: 'Landing',
  other: 'Інше',
}

const renderTimestamp = (value) => {
  if (!value?.seconds) {
    return '—'
  }

  return formatDateAsDayMontYearHHMM(value)
}

function LinksTable({ links = [], isLoading, history, onNotify }) {
  const { user } = useContext(AuthContext)
  const [linkToDelete, setLinkToDelete] = useState(null)

  const canEditLink = useCallback(
    (row) => canEditCampaignLink({ uid: user?.uid, responsible: row.responsible }),
    [user?.uid]
  )

  const openEdit = (row) => {
    if (!canEditLink(row)) {
      return
    }

    history.push(`/marketing/utm/${row.id}/edit`)
  }

  const openDelete = (row) => {
    if (!canEditLink(row)) {
      return
    }

    setLinkToDelete(row)
  }

  const columns = useMemo(
    () => [
      {
        field: 'createdAtSort',
        headerName: 'Створено sort',
        type: 'number',
      },
      {
        field: 'createdAt',
        headerClassName: 'TableHeadCol',
        headerName: 'Створено',
        width: 170,
        renderCell: (params) => renderTimestamp(params.row.createdAt),
      },
      {
        field: 'isActive',
        headerClassName: 'TableHeadCol',
        headerName: 'Стан',
        width: 110,
        renderCell: (params) => <ActiveStatusBadge isActive={params.row.isActive} />,
      },
      {
        field: 'title',
        headerClassName: 'TableHeadCol',
        headerName: 'Назва кампанії',
        width: 300,
        maxWidth: 300,
        cellClassName: 'MarketingLinks-TitleCellCol',
        renderCell: (params) => (
          <span className="MarketingLinks-TitleCell">{params.row.title || '—'}</span>
        ),
      },
      {
        field: 'shortUrl',
        headerClassName: 'TableHeadCol',
        headerName: 'Коротке посилання',
        width: 320,
        sortable: false,
        renderCell: (params) => <ShortLinkCell shortUrl={params.row.shortUrl} />,
      },
      {
        field: 'channel',
        headerClassName: 'TableHeadCol',
        headerName: 'Канал',
        width: 120,
        renderCell: (params) => CHANNEL_LABELS[params.row.channel] || params.row.channel || '—',
      },
      {
        field: 'format',
        headerClassName: 'TableHeadCol',
        headerName: 'Формат',
        width: 110,
        renderCell: (params) => FORMAT_LABELS[params.row.format] || params.row.format || '—',
      },
      {
        field: 'direction',
        headerClassName: 'TableHeadCol',
        headerName: 'Напрямок',
        width: 140,
        renderCell: (params) => DIRECTION_LABELS[params.row.direction] || params.row.direction || '—',
      },
      {
        field: 'context',
        headerClassName: 'TableHeadCol',
        headerName: 'Контекст',
        minWidth: 180,
        flex: 1,
        sortable: false,
        cellClassName: 'MarketingLinks-ContextCellCol',
        renderCell: (params) => <LinkContextCell row={params.row} />,
      },
      {
        field: 'responsibleLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Відповідальний',
        width: 130,
        renderCell: (params) => resolveCampaignLinkResponsibleLabel(params.row),
      },
      {
        field: 'updatedAt',
        headerClassName: 'TableHeadCol',
        headerName: 'Оновлено',
        width: 170,
        renderCell: (params) => renderTimestamp(params.row.updatedAt),
      },
      {
        field: 'actions',
        headerClassName: 'TableHeadCol',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          canEditLink(params.row) ? (
            <LinksRowActions
              onEdit={() => openEdit(params.row)}
              onDelete={() => openDelete(params.row)}
            />
          ) : null,
      },
    ],
    [canEditLink]
  )

  const closeDeletePopUp = () => setLinkToDelete(null)

  const handleDeleteError = (message) => {
    onNotify?.({
      show: true,
      type: 'fail',
      message: message || 'Не вдалося видалити посилання',
      closeAfter: 6000,
    })
  }

  return (
    <>
      {linkToDelete && (
        <DeleteCampaignLinkPopUp
          link={linkToDelete}
          closePopUp={closeDeletePopUp}
          onError={handleDeleteError}
        />
      )}
    <ModulePanel
      title="Посилання / UTM"
      buttons={
        <Button
          title="Створити посилання"
          theme="solid"
          fill="accent"
          size={32}
          onClick={() => history.push('/marketing/utm/new')}
        />
      }
    >
      {isLoading ? (
        <Spinner type="module" />
      ) : (
        <div className="MarketingLinks-Table">
          <DataTable
            initialState={{
              sorting: {
                sortModel: [{ field: 'createdAtSort', sort: 'desc' }],
              },
              columns: {
                columnVisibilityModel: {
                  createdAtSort: false,
                },
              },
            }}
            table={{
              rows: links,
              columns,
              autoHeight: true,
              onRowDoubleClick: (params) => openEdit(params.row),
            }}
          />
        </div>
      )}
    </ModulePanel>
    </>
  )
}

export default withRouter(LinksTable)
